/*
 * This module manages the join hotkey which allows users to quickly join the next meeting
 * by pressing a hotkey.
 */

import { Ref } from "vue";

import { Controller, FullEvent } from "@ctrl/controller-rpc";

import { useConference } from "@/composables/useConference";
import { in_electron } from "@/utils/in";

/*
 * Constants
 */

// The number of minutes ahead of the meeting which is allowed
// to consider this meeting "joinable".
const MINUTES_AHEAD = 15;

const IPC_INTERFACE = {
  CALL: "hotkey:join:call",
};

/*
 * Module
 */
export const join = {
  IPC_INTERFACE,

  setup(controller: Ref<Controller | null>) {
    if (!in_electron) return;

    async function callback() {
      if (!controller.value) return;

      const _today = await controller.value.today();
      const _now = await controller.value.now();

      // Compute the ahead time limit.
      //
      // We allow the user to join a meeting which hasn't started yet only if it will
      // start in the next MINUTES_AHEAD minutes.
      const limit = new Date(_now.value);
      limit.setMinutes(limit.getMinutes() + MINUTES_AHEAD);

      const _timeline = await controller.value.event.timeline(_today.value);

      _today.release();

      const meetings = _timeline.value.filter(
        (item): item is FullEvent & { type: "event" } => {
          if (item.type !== "event") return false;
          const { end, link, start } = item;
          return (
            item.allDay === false &&
            Boolean(start) &&
            // Either the meeting has started or it will start in the next MINUTES_AHEAD minutes
            (new Date(start) < _now.value || new Date(start) < limit) &&
            Boolean(end) &&
            // The meeting has not ended yet i.e is not in the past
            new Date(end) > _now.value &&
            item.attendable === true &&
            link?.url !== undefined
          );
        }
      );

      _now.release();
      _timeline.release();

      // If there is an upcoming meeting, open the conference link URL.
      if (meetings.length > 0) {
        const nextMeeting = meetings[0];
        const conference = await useConference(controller.value, nextMeeting);
        if (conference) {
          await conference.open();
        }
      }
    }

    // Set up the IPC handler for the Electron process to trigger the
    // callback in the renderer.
    const { ipcRenderer: ipcClient } = require("electron");
    ipcClient.on(IPC_INTERFACE.CALL, callback);
  },
};
